import { API_ENDPOINTS } from '@api/ApiEndpoints';
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  ServiceIntegrationMSData,
  ServiceIntegrationRCData,
  ServiceIntegrationUCData,
  ServiceIntegrationReconnectMSData,
  ServiceIntegrationReconnectRCData,
  ServiceIntergrationResponse,
  HelixOnboardingResponse,
  HelixOnboardingRequest,
} from './types';
import { useHttpClient } from '@hooks/utils/httpConfig';

export function useConnectedServicesQuery(): UseQueryResult<
  ServiceIntergrationResponse[]
> {
  const connectedServicesClient = useHttpClient();
  const fetchConnectedServices = async (): Promise<
    ServiceIntergrationResponse[]
  > => {
    return connectedServicesClient.get<ServiceIntergrationResponse[]>(
      API_ENDPOINTS.SERVICE_INTEGRATIONS
    );
  };

  return useQuery({ queryKey: ['connectedServices'], queryFn: fetchConnectedServices });
}

export const useServiceIntegrationMS = (): UseMutationResult<
  ServiceIntergrationResponse,
  unknown,
  ServiceIntegrationMSData
> => {
  const connectedServicesClient = useHttpClient();
  return useMutation({
    mutationFn: (data: ServiceIntegrationMSData) =>
      connectedServicesClient.post<ServiceIntergrationResponse>(
        `${API_ENDPOINTS.SERVICE_INTERGRATION_MS}`,
        {
          tenantId: data.tenantId,
        }
      ),
  });
};

export const useServiceIntegrationReconnectMS = (): UseMutationResult<
  ServiceIntergrationResponse,
  unknown,
  ServiceIntegrationReconnectMSData
> => {
  const connectedServicesClient = useHttpClient();
  return useMutation({ mutationFn: (data: ServiceIntegrationReconnectMSData) =>
    connectedServicesClient.patch<ServiceIntergrationResponse>(
      `${API_ENDPOINTS.SERVICE_INTEGRATIONS}/${data.connectionId}/reconnect/microsoft`,
      {
        tenantId: data.tenantId,
      }
    ) });
};

export const useServiceIntegrationReconnectHelix = (): UseMutationResult<
  ServiceIntergrationResponse,
  unknown,
  ServiceIntegrationReconnectMSData
> => {
  const connectedServicesClient = useHttpClient();
  return useMutation({ mutationFn: (data: ServiceIntegrationReconnectMSData) =>
    connectedServicesClient.patch<ServiceIntergrationResponse>(
      `${API_ENDPOINTS.SERVICE_INTEGRATIONS}/${data.connectionId}/reconnect/helix`,
      {
        tenantId: data.tenantId,
      }
    ) });
};

export const useServiceIntegrationReconnectRC = (): UseMutationResult<
  ServiceIntergrationResponse,
  unknown,
  ServiceIntegrationReconnectRCData
> => {
  const connectedServicesClient = useHttpClient();
  return useMutation({ mutationFn: (data: ServiceIntegrationReconnectRCData) =>
    connectedServicesClient.patch<ServiceIntergrationResponse>(
      `${API_ENDPOINTS.SERVICE_INTEGRATIONS}/${data.connectionId}/reconnect/ringcentral`,
      {
        jwt: data.jwt,
      }
    ) });
};

export const useServiceIntegrationRC = (): UseMutationResult<
  ServiceIntergrationResponse,
  unknown,
  ServiceIntegrationRCData
> => {
  const connectedServicesClient = useHttpClient();
  return useMutation({ mutationFn: (data: ServiceIntegrationRCData) =>
    connectedServicesClient.post<ServiceIntergrationResponse>(
      `${API_ENDPOINTS.SERVICE_INTERGRATION_RC}`,
      {
        jwt: data.jwt,
      }
    ) });
};

export const useServiceIntegrationUC = (): UseMutationResult<
  ServiceIntergrationResponse,
  unknown,
  ServiceIntegrationUCData
> => {
  const connectedServicesClient = useHttpClient();
  return useMutation({ mutationFn: (data: ServiceIntegrationUCData) =>
    connectedServicesClient.post<ServiceIntergrationResponse>(
      `${API_ENDPOINTS.SERVICE_INTERGRATION_UC}`,
      {
        vendor: data.vendor
      }
    ) });
};

export const useCheckMsAuditingEnabled = (): UseMutationResult<
  boolean,
  unknown,
  { tenantId: string }
> => {
  const httpClient = useHttpClient();
  return useMutation({ mutationFn: (data: { tenantId: string }) =>
    httpClient.post<boolean>(
      `${API_ENDPOINTS.SERVICE_INTEGRATIONS}/auditing-check`,
      {
        tenantId: data.tenantId
      }
    ) });
};

export const useDeactivateService = () => {
  const connectedServicesClient = useHttpClient();
  return useMutation({ mutationFn: (connectionId: string) =>
    connectedServicesClient.patch(
      `${API_ENDPOINTS.SERVICE_INTERGRATION_STATUS}/${connectionId}/deactivate`,
      null
    ) });
};

export const useReactivateService = () => {
  const connectedServicesClient = useHttpClient();
  return useMutation({ mutationFn: (connectionId: string) =>
    connectedServicesClient.patch(
      `${API_ENDPOINTS.SERVICE_INTERGRATION_STATUS}/${connectionId}/reactivate`,
      null
    ) });
};

export const useCancelServiceUC = () => {
  const connectedServicesClient = useHttpClient();
  return useMutation({ mutationFn: (connectionId: string) =>
    connectedServicesClient.patch(
      `${API_ENDPOINTS.SERVICE_INTERGRATION_STATUS}/${connectionId}/cancel`,
      null
    ) });
};

export const useHelixOnboarding = (): UseMutationResult<
  HelixOnboardingResponse,
  unknown,
  HelixOnboardingRequest
> => {
  const httpClient = useHttpClient();
  
  return useMutation({ mutationFn: (data: HelixOnboardingRequest) =>
    httpClient.post<HelixOnboardingResponse>(
      API_ENDPOINTS.SERVICE_HELIX_ONBOARDING,
      {
        tenantId: data.tenantId        
      }
    ) });
};

export const useSendNoServicesConnectedEmail = (): UseMutationResult<
  boolean,
  unknown,
  void
> => {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: () =>
      httpClient.post<boolean>(
        API_ENDPOINTS.EMAIL_NOTIFICATIONS_NO_SERVICES_CONNECTED_EMAIL,
        {}
      ),
  });
};